<template>
  <!--
    The only design-system entry point for Nuxt UI's FormField (atomic-boundaries).
    Forwards only; app-level behavior belongs to FormField.molecule.vue.
  -->
  <UFormField
    :name="name"
    :label="label"
    :required="required"
    :description="description"
    :hint="hint"
    :orientation="orientation"
    :help="help"
    :ui="ui"
    v-bind="errorBinding"
  >
    <template
      v-if="$slots['label']"
      #label
    >
      <slot name="label" />
    </template>

    <template
      v-if="$slots['hint']"
      #hint
    >
      <slot name="hint" />
    </template>

    <template
      v-if="$slots['help']"
      #help
    >
      <slot name="help" />
    </template>

    <template
      v-if="$slots['error']"
      #error
    >
      <slot name="error" />
    </template>

    <VendorFormFieldControl>
      <template #default="control">
        <slot
          :id="control.id"
          :describedby="control.describedby"
          :invalid="control.invalid"
        />
      </template>
    </VendorFormFieldControl>
  </UFormField>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import UFormField from '@nuxt/ui/components/FormField.vue'

import { omitEmptyStringProps } from './omitEmptyStringProps'
import VendorFormFieldControl from './VendorFormFieldControl.base.vue'

const props = withDefaults(
  defineProps<{
    /** Field name. Nuxt UI also matches form errors against it. */
    name: string
    /** Visible label text, already translated. */
    label: string
    /** Renders the required marker and sets aria-required. */
    required?: boolean
    /**
     * Secondary guidance rendered under the label. Empty means absent: Nuxt UI
     * renders the description block only for a truthy value.
     */
    description?: string
    /** Short text rendered beside the label. */
    hint?: string
    /**
     * Validation message. Drives aria-invalid and aria-describedby. Empty
     * means valid -- see errorBinding for why it cannot simply be forwarded.
     */
    error?: string
    /**
     * Guidance rendered under the control rather than under the label. Nuxt UI
     * drops it while an error is showing, which takes its place.
     */
    help?: string
    /** Horizontal places the label beside the control instead of above it. */
    orientation?: 'horizontal' | 'vertical'
    /** Field slots. The molecule owns these; app code does not set them. */
    ui?: {
      container?: string
      description?: string
      error?: string
      help?: string
      hint?: string
      label?: string
      labelWrapper?: string
      root?: string
    }
  }>(),
  {
    description: '',
    error: '',
    help: '',
    hint: '',
    orientation: 'vertical',
    required: false,
    ui: () => ({})
  }
)

// Nuxt UI casts `error=""` to `true` (declared `[Boolean, String]`), so omit rather than forward an empty error.
const errorBinding = computed(() => omitEmptyStringProps({ error: props.error }))

defineSlots<{
  /** The control itself, handed the ARIA wiring Nuxt UI computed for it. */
  default?: (props: { id: string; describedby: string | undefined; invalid: boolean }) => unknown
  /** Replaces the rendered label, e.g. to wrap it in a tooltip trigger. */
  label?: () => unknown
  /** Replaces the hint slot, e.g. to host a state indicator. */
  hint?: () => unknown
  /** Replaces the help copy under the control. */
  help?: () => unknown
  /** Replaces the error copy, e.g. to show help beside it. */
  error?: () => unknown
}>()
</script>
